import * as path from "node:path";
import * as fsp from "node:fs/promises";
import * as fs from "node:fs";
import fs2 from "node:fs";
import { OutputContainerSchema, OutputSettingsSchema, OutputWorkerSchema } from "@cloudflare/config";

//#region src/paths.ts
/**
* Initial draft version of the Build Output Specification.
*
* Will move to `v1` when the spec stabilises.
*/
const BUILD_OUTPUT_VERSION = "v0";
/**
* Project-relative root.
*/
const BUILD_OUTPUT_ROOT = ".cloudflare/output";
/**
* Filename shared by every config in the Build Output Specification.
*
* Configs are discriminated by their `type` field.
*/
const CONFIG_FILENAME = "config.json";
/**
* Name of the sub-directory under `workers/` holding the default Worker.
*/
const DEFAULT_WORKER_DIRECTORY_NAME = "default";
/**
* Absolute path to the Build Output Specification root for the current project.
*/
function getBuildOutputDir(root) {
	return path.resolve(root, BUILD_OUTPUT_ROOT);
}
/**
* Absolute path to the top-level `config.json` for the current project.
*
* Holds the project-level settings shared by every Worker: those declared by
* the `settings` export of the input `cloudflare.config.ts`, including the
* mode the build was produced in.
*/
function getSettingsConfigPath(root) {
	return path.join(getBuildOutputDir(root), BUILD_OUTPUT_VERSION, CONFIG_FILENAME);
}
/**
* Absolute path to the Workers output directory.
*/
function getWorkersDir(root) {
	return path.join(getBuildOutputDir(root), BUILD_OUTPUT_VERSION, "workers");
}
/**
* Absolute path to the Containers output directory.
*/
function getContainersDir(root) {
	return path.join(getBuildOutputDir(root), BUILD_OUTPUT_VERSION, "containers");
}
function validateDirectoryName(name, resourceName) {
	if (name.length === 0 || name === "." || name === ".." || name.includes("/") || name.includes("\\") || name.includes("\0")) throw new Error(`${resourceName} directory names must be non-empty, single path segments. Received ${JSON.stringify(name)}.`);
}
/**
* Absolute path to a Worker's directory (`workers/<worker-directory-name>`).
*/
function getWorkerDir(root, workerDirectoryName = DEFAULT_WORKER_DIRECTORY_NAME) {
	validateDirectoryName(workerDirectoryName, "Worker");
	return path.join(getWorkersDir(root), workerDirectoryName);
}
/**
* Absolute path to a Container's directory
* (`containers/<container-directory-name>`).
*/
function getContainerDir(root, containerDirectoryName) {
	validateDirectoryName(containerDirectoryName, "Container");
	return path.join(getContainersDir(root), containerDirectoryName);
}
/**
* Absolute path to the Container's config file.
*/
function getContainerConfigPath(root, containerDirectoryName) {
	return path.join(getContainerDir(root, containerDirectoryName), CONFIG_FILENAME);
}
/**
* Absolute path to the Worker's config file.
*/
function getWorkerConfigPath(root, workerDirectoryName = DEFAULT_WORKER_DIRECTORY_NAME) {
	return path.join(getWorkerDir(root, workerDirectoryName), CONFIG_FILENAME);
}
/**
* Absolute path to the Worker's bundle directory.
*/
function getWorkerBundleDir(root, workerDirectoryName = DEFAULT_WORKER_DIRECTORY_NAME) {
	return path.join(getWorkerDir(root, workerDirectoryName), "bundle");
}
/**
* Absolute path to the Worker's assets directory.
*/
function getWorkerAssetsDir(root, workerDirectoryName = DEFAULT_WORKER_DIRECTORY_NAME) {
	return path.join(getWorkerDir(root, workerDirectoryName), "assets");
}

//#endregion
//#region ../workers-utils/dist/chunk-Q72B4Q5Z.mjs
var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", {
	value,
	configurable: true
});

//#endregion
//#region ../workers-utils/dist/chunk-53IMCORZ.mjs
function isDirectory(path$1) {
	return fs2.statSync(path$1, { throwIfNoEntry: false })?.isDirectory() ?? false;
}
__name(isDirectory, "isDirectory");
function removeDir(dirPath, { fireAndForget = false } = {}) {
	const result = fs2.promises.rm(dirPath, {
		recursive: true,
		force: true,
		maxRetries: 5,
		retryDelay: 100
	});
	if (fireAndForget) result.catch(() => {});
	else return result;
}
__name(removeDir, "removeDir");
function removeDirSync(dirPath) {
	fs2.rmSync(dirPath, {
		recursive: true,
		force: true,
		maxRetries: 5,
		retryDelay: 100
	});
}
__name(removeDirSync, "removeDirSync");

//#endregion
//#region src/write.ts
/**
* Clean the build output directory.
*/
async function cleanBuildOutputDir(root) {
	await removeDir(getBuildOutputDir(root));
}
/**
* Write an output Worker `config.json` to the Build Output Specification tree.
*
* - Workers mode: `manifest` is provided (bundle/ present on disk).
* - Assets-only mode: `manifest` is omitted (no bundle/ directory).
*/
async function writeWorkerConfig({ root, config, manifest, directoryName = DEFAULT_WORKER_DIRECTORY_NAME }) {
	const { entrypoint: _entrypoint,...rest } = config;
	const outputConfig = {
		...rest,
		manifest
	};
	await fsp.mkdir(getWorkerDir(root, directoryName), { recursive: true });
	await fsp.writeFile(getWorkerConfigPath(root, directoryName), JSON.stringify(outputConfig));
}
/**
* Write an output Container `config.json` to the Build Output Specification
* tree.
*
* Local Dockerfiles must already have been built and represented by a
* `localReference` in the output config.
*/
async function writeContainerConfig({ root, config, directoryName }) {
	await fsp.mkdir(getContainerDir(root, directoryName), { recursive: true });
	await fsp.writeFile(getContainerConfigPath(root, directoryName), JSON.stringify(config));
}
/**
* Write the top-level `config.json` to the Build Output Specification tree.
*
* Holds the project settings shared by every Worker, plus the build mode and
* whether the build is for a Preview. Always written, even without declared
* settings or a mode.
*
* `mode` is omitted when undefined, which is the case for Wrangler builds that
* selected no mode (Vite always resolves one).
*/
async function writeSettingsConfig(root, settings, mode, isPreview = false) {
	const outputConfig = {
		...settings,
		type: "settings",
		...isPreview ? { isPreview: true } : {},
		...mode !== void 0 ? { mode } : {}
	};
	const configPath = getSettingsConfigPath(root);
	await fsp.mkdir(path.dirname(configPath), { recursive: true });
	await fsp.writeFile(configPath, JSON.stringify(outputConfig));
}

//#endregion
//#region src/errors.ts
const BUILD_OUTPUT_ERROR_PREFIX = "Build Output Specification: ";
/**
* Thrown when the Build Output Specification tree is missing, malformed, or
* fails schema validation while being read.
*/
var BuildOutputError = class extends Error {
	constructor(message) {
		super(`${BUILD_OUTPUT_ERROR_PREFIX}${message}`);
		this.name = "BuildOutputError";
	}
};

//#endregion
//#region src/read.ts
/**
* Read and validate the Build Output Specification tree at
* `<root>/.cloudflare/output/v0/`.
*
* Reads the optional top-level settings `config.json`, then reads and
* schema-validates each Worker and Container `config.json`, and resolves each
* Worker's `bundle/` / `assets/` directories. Partial manifests are resolved
* into complete manifests using the files in `bundle/`.
*
* @throws {BuildOutputError} if the top-level `config.json` is invalid, or if
* a Worker or Container config is missing, is not valid JSON, or fails schema
* validation.
*/
async function readBuildOutput(root) {
	const settings = await readSettings(root);
	const defaultWorker = await readWorker(root, DEFAULT_WORKER_DIRECTORY_NAME);
	const additionalWorkerDirectoryNames = (await fsp.readdir(getWorkersDir(root), { withFileTypes: true })).filter((entry) => entry.isDirectory() && entry.name !== DEFAULT_WORKER_DIRECTORY_NAME).map((entry) => entry.name).sort();
	const additionalWorkers = await Promise.all(additionalWorkerDirectoryNames.map(async (workerDirectoryName) => [workerDirectoryName, await readWorker(root, workerDirectoryName)]));
	return {
		root,
		version: BUILD_OUTPUT_VERSION,
		settings,
		workers: {
			default: defaultWorker,
			...Object.fromEntries(additionalWorkers)
		},
		containers: await readContainers(root)
	};
}
/** Read and schema-validate all Container configs, if any. */
async function readContainers(root) {
	const containersDir = getContainersDir(root);
	if (!fs.existsSync(containersDir)) return {};
	const containerDirectoryNames = (await fsp.readdir(containersDir, { withFileTypes: true })).filter((entry) => entry.isDirectory()).map((entry) => entry.name).sort();
	const containers = await Promise.all(containerDirectoryNames.map(async (containerDirectoryName) => [containerDirectoryName, await readContainer(root, containerDirectoryName)]));
	return Object.fromEntries(containers);
}
/** Read and schema-validate one Container config. */
async function readContainer(root, containerDirectoryName) {
	const configPath = getContainerConfigPath(root, containerDirectoryName);
	if (!fs.existsSync(configPath)) throw new BuildOutputError(`no Container config found at ${configPath}.`);
	const contents = await fsp.readFile(configPath, "utf-8");
	const result = OutputContainerSchema.safeParse(parseJson(contents, configPath));
	if (!result.success) throw new BuildOutputError(`invalid Container config at ${configPath}.\n${result.error.message}`);
	return {
		configPath,
		config: result.data
	};
}
/**
* Read and schema-validate the Worker's `config.json` and resolve its
* `bundle/` / `assets/` directories.
*
* @returns the Worker, with whichever of its directories exist resolved.
* @throws {BuildOutputError} if the config is missing, is not valid JSON, or
* fails schema validation.
*/
async function readWorker(root, workerDirectoryName) {
	const configPath = getWorkerConfigPath(root, workerDirectoryName);
	if (!fs.existsSync(configPath)) throw new BuildOutputError(`no Worker config found at ${configPath}.`);
	const contents = await fsp.readFile(configPath, "utf-8");
	const result = OutputWorkerSchema.safeParse(parseJson(contents, configPath));
	if (!result.success) throw new BuildOutputError(`invalid Worker config at ${configPath}.\n${result.error.message}`);
	const bundleDir = getWorkerBundleDir(root, workerDirectoryName);
	const assetsDir = getWorkerAssetsDir(root, workerDirectoryName);
	const hasBundleDir = fs.existsSync(bundleDir);
	const hasAssetsDir = fs.existsSync(assetsDir);
	if (result.data.manifest && !hasBundleDir) throw new BuildOutputError(`Worker config at ${configPath} contains a manifest, but no bundle directory exists at ${bundleDir}.`);
	if (hasBundleDir) return {
		configPath,
		config: await resolveManifest(result.data, configPath, bundleDir),
		bundleDir,
		assetsDir: hasAssetsDir ? assetsDir : void 0
	};
	if (hasAssetsDir) {
		const { manifest: _manifest,...config } = result.data;
		return {
			configPath,
			config,
			bundleDir: void 0,
			assetsDir
		};
	}
	throw new BuildOutputError(`Worker config at ${configPath} has neither a bundle directory (${bundleDir}) nor an assets directory (${assetsDir}).`);
}
/** Resolve the manifest against the bundle contents. */
async function resolveManifest(config, configPath, bundleDir) {
	const { manifest,...workerConfig } = config;
	if (manifest === void 0) return workerConfig;
	if (manifest.type === "complete") return {
		...workerConfig,
		manifest: {
			...manifest,
			type: "complete"
		}
	};
	const inferredModules = await scanModules(bundleDir);
	if (inferredModules[manifest.mainModule]?.type !== "esm") throw new BuildOutputError(`partial manifest at ${configPath} has main module "${manifest.mainModule}", but it was not found as an ES module in the bundle.`);
	const modules = {
		...inferredModules,
		...manifest.modules
	};
	return {
		...workerConfig,
		manifest: {
			type: "complete",
			mainModule: manifest.mainModule,
			modules
		}
	};
}
/** Infer JavaScript modules and source maps from a bundle directory. */
async function scanModules(bundleDir) {
	const modules = {};
	const entries = await fsp.readdir(bundleDir, {
		recursive: true,
		withFileTypes: true
	});
	for (const entry of entries) {
		const type = entry.isFile() ? inferModuleType(entry.name) : void 0;
		if (type !== void 0) {
			const modulePath = path.relative(bundleDir, path.join(entry.parentPath, entry.name)).split(path.sep).join("/");
			modules[modulePath] = { type };
		}
	}
	return modules;
}
/** Infer the module type for extensions supported by partial manifests. */
function inferModuleType(modulePath) {
	switch (path.extname(modulePath)) {
		case ".js":
		case ".mjs": return "esm";
		case ".map": return "sourcemap";
	}
}
/**
* Read and schema-validate the optional top-level `config.json` holding the
* project-level settings shared by every Worker, including the mode the build
* was produced in.
*
* Returned whole: consumers that need only the settings the user declared
* narrow it themselves.
*
* @returns the parsed settings, or `undefined` when the file is absent.
* @throws {BuildOutputError} if the file is not valid JSON or fails schema
* validation.
*/
async function readSettings(root) {
	const configPath = getSettingsConfigPath(root);
	if (!fs.existsSync(configPath)) return;
	const contents = await fsp.readFile(configPath, "utf-8");
	const result = OutputSettingsSchema.safeParse(parseJson(contents, configPath));
	if (!result.success) throw new BuildOutputError(`invalid settings config at ${configPath}.\n${result.error.message}`);
	return result.data;
}
function parseJson(contents, configPath) {
	try {
		return JSON.parse(contents);
	} catch (e) {
		throw new BuildOutputError(`could not parse JSON at ${configPath}.\n${e instanceof Error ? e.message : String(e)}`);
	}
}

//#endregion
export { BUILD_OUTPUT_ROOT, BUILD_OUTPUT_VERSION, BuildOutputError, CONFIG_FILENAME, DEFAULT_WORKER_DIRECTORY_NAME, cleanBuildOutputDir, getContainerConfigPath, getContainerDir, getContainersDir, getSettingsConfigPath, getWorkerAssetsDir, getWorkerBundleDir, getWorkerConfigPath, getWorkerDir, getWorkersDir, readBuildOutput, writeContainerConfig, writeSettingsConfig, writeWorkerConfig };